import React, { createContext, useContext, useState, ReactNode } from "react";
import Snackbar from "@mui/material/Snackbar";
import { Alert, CircularProgress } from "@mui/material";

type AlertColor = "success" | "error" | "warning" | "info";

function toAlertText(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (typeof value === "object") {
    const record = value as { message?: unknown };
    if (record.message !== undefined && record.message !== value) {
      return toAlertText(record.message);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "Произошла ошибка";
    }
  }
  return String(value);
}

type AlertContextType = (
  message: unknown,
  severity?: AlertColor,
  isProgress?: boolean
) => void;

const AlertContext = createContext<
  { showAlert: AlertContextType; hideAlert: () => void } | undefined
>(undefined);

export const useAlert = (): {
  showAlert: AlertContextType;
  hideAlert: () => void;
} => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAlert must be used within an AlertProvider");
  }
  return context;
};

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<AlertColor>("info");
  const [isProgress, setProgress] = useState(false);

  const showAlert: AlertContextType = (
    newMessage,
    newSeverity = "info",
    isProgress = false
  ) => {
    setMessage(toAlertText(newMessage));
    setSeverity(newSeverity);
    setOpen(true);
    setProgress(isProgress);
  };

  const hideAlert = () => {
    setOpen(false);
  };

  return (
    <AlertContext.Provider value={{ showAlert, hideAlert }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={!isProgress ? 10000 : null}
        onClose={hideAlert}
        // message={message}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        style={{ maxWidth: "400px" }}
        // Assuming severity should be a string, but it's not a valid prop for Snackbar
      >
        <Alert
          severity={severity as AlertColor}
          onClose={hideAlert}
          variant="standard"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "12px",
              whiteSpace: "normal",
            }}
          >
            {isProgress ? <CircularProgress size={"24px"} /> : null}
            <span>{message}</span>
          </div>
        </Alert>
      </Snackbar>
    </AlertContext.Provider>
  );
};
